import '../../styles/bitacora.style.css'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import BasicCardDocumentos from '../DatosDeCierre/BasicCardDocumentos'
import { State } from '../../../../reducers'
import { editClasificacionSujetosExcluidos, getComprasSujetosExcluidos } from '../../../../actions/libros/libro-compras.actions'
import { PanelClasificacionCompras, SelectClasificacion } from '../LibroCompras/PanelClasificacionCompras'
import { CAMPOS_CODIGO, CampoCodigo, Codigos, calcularAplicacion, esCodigoValido, mensajeAplicacion, periodoExigeClasificacion } from '../../../../utils/codificacion-compras.utils'

// Nombre de cada dato en lo que devuelve el anexo 5 (SP_GetAnexoComprasFSE)
const CAMPO_ANEXO5: Record<CampoCodigo, string> = {
  tipoOperacion: 'tipooperacion',
  clasificacion: 'clasificacion',
  sector: 'sector',
  tipoClasificacion: 'tipocostogasto',
};
const TIPO_DOCUMENTO: Record<string, string> = { '1': 'NIT', '2': 'DUI', '3': 'Otro' };
const formatearFecha = (fecha: string) => (/^\d{4}-\d{2}-\d{2}/.test(fecha ?? '') ? fecha.slice(0, 10).split('-').reverse().join('/') : fecha);

// Compras a sujetos excluidos (ANEXO 5 del libro de compras): facturas de sujeto excluido que emitio la empresa en el periodo declarado.
// Se clasifican igual que las compras; el CSV del anexo 5 sale junto con el del anexo 3 al descargar el libro.
const TableComprasSujetosExcluidos = () => {
  const {comprasLibroActivo} = useSelector((state: State) => state.libros);
  const {libroActivo} = comprasLibroActivo;
  const [facturas, setFacturas] = useState<Array<any> | null>(null);
  // Cambios sin guardar por factura (clave: numerodocumento = codigo de generacion sin guiones)
  const [cambios, setCambios] = useState<Record<string, Partial<Codigos>>>({});
  const [versionPanel, setVersionPanel] = useState<number>(0);
  const [errorCarga, setErrorCarga] = useState<boolean>(false);

  const dispatch = useDispatch();
  const exige = periodoExigeClasificacion(libroActivo?.fechadeclaracion);

  const cargar = async () => {
    if (!libroActivo?.idlibrocompras) return;
    const resultado: any = await dispatch(getComprasSujetosExcluidos(libroActivo.idlibrocompras) as any);
    setErrorCarga(resultado === null);
    setFacturas(resultado ?? []);
  }

  // Al abrir otro libro se recarga la lista y se descartan los cambios sin guardar
  useEffect(() => {
    setCambios({});
    setFacturas(null);
    cargar();
  }, [libroActivo?.idlibrocompras]);

  const lista: Array<any> = facturas ?? [];
  const valorCodigo = (item: any, campo: CampoCodigo): number => Number(cambios[item.numerodocumento]?.[campo] ?? item[CAMPO_ANEXO5[campo]] ?? 0) || 0;
  const guardado = (item: any, campo: CampoCodigo): number => Number(item[CAMPO_ANEXO5[campo]] ?? 0) || 0;
  const tieneCambios = (item: any): boolean => CAMPOS_CODIGO.some((campo) => valorCodigo(item, campo) !== guardado(item, campo));
  const pendientes = lista.filter((item) => CAMPOS_CODIGO.some((campo) => !esCodigoValido(campo, valorCodigo(item, campo)))).length;
  const facturasConCambios = lista.filter(tieneCambios);

  const handleCambioCodigo = (item: any, campo: CampoCodigo, valor: number) => {
    setCambios((prev) => ({ ...prev, [item.numerodocumento]: { ...prev[item.numerodocumento], [campo]: valor } }));
  }

  const handleAplicar = (masivo: Codigos, soloSinClasificar: boolean): string => {
    const { parches, compras } = calcularAplicacion(lista, (item) => String(item.numerodocumento), valorCodigo, masivo, soloSinClasificar);
    setCambios((prev) => {
      const siguiente = { ...prev };
      Object.entries(parches).forEach(([clave, parche]) => { siguiente[clave] = { ...siguiente[clave], ...parche }; });
      return siguiente;
    });
    return mensajeAplicacion(compras).replace('compras', 'facturas').replace('compra.', 'factura.') + (compras > 0 ? ' Pulse "Guardar cambios" para conservarlo.' : '');
  }

  const handleGuardar = async () => {
    const items = facturasConCambios.map((item) => ({
      numerodocumento: String(item.numerodocumento),
      tipooperacion: valorCodigo(item, 'tipoOperacion'),
      clasificacion: valorCodigo(item, 'clasificacion'),
      sector: valorCodigo(item, 'sector'),
      tipoclasificacion: valorCodigo(item, 'tipoClasificacion'),
    }));
    if (await dispatch(editClasificacionSujetosExcluidos(libroActivo, items) as any)) {
      setCambios({});
      setVersionPanel((v) => v + 1);
      await cargar();
    }
  }

  // Sin facturas de sujeto excluido en el periodo no hay nada que mostrar
  if (facturas === null && !errorCarga) return null;
  if (errorCarga) {
    return <p className="s" style={{ color: '#7a4b00', margin: '16px 0' }}>No se pudieron consultar las compras a sujetos excluidos de este libro.</p>;
  }
  if (lista.length === 0) return null;

  return (
    <div className="w-layout-layout bill-section wf-layout-layout" style={{ marginTop: '24px' }}>
      <BasicCardDocumentos style={{boxShadow: 'none', padding: '0'}} title="Compras a sujetos excluidos" size="l">
        <p className="s" style={{ margin: '0 0 12px', color: '#4a5562' }}>
          Facturas de sujeto excluido emitidas en el período declarado ({lista.length}). Salen en el anexo 5 del libro; el monto es el valor de la compra antes de la retención de renta.
        </p>
        <PanelClasificacionCompras
          key={versionPanel}
          exige={exige}
          total={lista.length}
          pendientes={pendientes}
          onAplicar={handleAplicar}
          pie={exige && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
              <button className="button w-auto" disabled={facturasConCambios.length === 0} onClick={handleGuardar}>Guardar cambios</button>
              <span className="s" style={{ color: facturasConCambios.length > 0 ? '#7a4b00' : '#4a5562' }}>
                {facturasConCambios.length > 0
                  ? `${facturasConCambios.length} ${facturasConCambios.length === 1 ? 'factura con cambios' : 'facturas con cambios'} sin guardar`
                  : 'No hay cambios sin guardar'}
              </span>
            </div>
          )}
        />
        <div className="tab-plane">
          <div className="table-facturacion-resp">
            <table className="table-main-responsive responsive-table-main td-left table-pointer">
              <thead>
                <tr>
                  <td>Fecha Emisión</td>
                  <td>Sujeto excluido</td>
                  <td>Tipo Doc.</td>
                  <td>Número Doc.</td>
                  <td>Monto de la operación</td>
                  <td>Retención IVA 13%</td>
                  <td>Tipo Operación</td>
                  <td>Clasificación</td>
                  <td>Tipo Costo/Gasto</td>
                  <td>Sector</td>
                </tr>
              </thead>
              <tbody>
                {lista.map((item: any) => (
                  <tr key={item.numerodocumento}>
                    <td data-label='Fecha Emisión'>{formatearFecha(item.fechaemision)}</td>
                    <td data-label='Sujeto excluido'>{item.nombrepersona}</td>
                    <td data-label='Tipo Doc.'>{TIPO_DOCUMENTO[String(item.tipodocumentopersona)] ?? item.tipodocumentopersona}</td>
                    <td data-label='Número Doc.'>{item.numerodocumentopersona}</td>
                    <td data-label='Monto de la operación'>{Number(item.montooperacion).toFixed(2)}</td>
                    <td data-label='Retención IVA 13%'>{Number(item.montoretencion).toFixed(2)}</td>
                    <td data-label='Tipo Operación'><SelectClasificacion campo='tipoOperacion' valor={valorCodigo(item, 'tipoOperacion')} exige={exige} onChange={(valor) => handleCambioCodigo(item, 'tipoOperacion', valor)} /></td>
                    <td data-label='Clasificación'><SelectClasificacion campo='clasificacion' valor={valorCodigo(item, 'clasificacion')} exige={exige} onChange={(valor) => handleCambioCodigo(item, 'clasificacion', valor)} /></td>
                    <td data-label='Tipo Costo/Gasto'><SelectClasificacion campo='tipoClasificacion' valor={valorCodigo(item, 'tipoClasificacion')} exige={exige} onChange={(valor) => handleCambioCodigo(item, 'tipoClasificacion', valor)} /></td>
                    <td data-label='Sector'><SelectClasificacion campo='sector' valor={valorCodigo(item, 'sector')} exige={exige} onChange={(valor) => handleCambioCodigo(item, 'sector', valor)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </BasicCardDocumentos>
    </div>
  )
}

export default TableComprasSujetosExcluidos
